import { ReactNode, useEffect } from "react";
import type { ToastTransitionProps } from "react-toastify";
import { toast, ToastIcon } from "react-toastify";

import {
  CircleCheckIcon,
  CircleCross,
  CircleInformation,
  SpinnerIcon
} from "@/assets/icons";

import { twMerge } from "tailwind-merge";

export enum EToastType {
  LOADING = "loading",
  SUCCESS = "success",
  ERROR = "error",
  WARNING = "warning"
}

type ToastType = "loading" | "error" | "success" | "warning";

interface ToastOptions {
  title?: string;
  message: string | ReactNode;
  type: ToastType;
  icon?: ToastIcon;
  autoClose?: number | false;
  className?: string;
  onClose?: (reason?: boolean | string) => void;
  id?: string;
  pauseOnFocusLoss?: boolean;
  pauseOnHover?: boolean;
  disableAnimation?: boolean;
}

const ToastNoTransition = ({
  children,
  done,
  isIn,
  playToast
}: ToastTransitionProps) => {
  useEffect(() => {
    if (isIn) {
      playToast();
      return;
    }

    done();
  }, [done, isIn, playToast]);

  return <>{children}</>;
};

const ToastContent = ({
  title,
  message
}: {
  title?: string;
  message: string | ReactNode;
}) => (
  <div>
    {title && (
      <p className="text-[1rem] font-semibold text-txtHeading">{title}</p>
    )}
    <span className="text-[0.875rem] text-txtHeading">{message}</span>
  </div>
);

// Plain utility — safe to call outside React
export const showToast = ({
  title,
  message,
  type,
  icon,
  autoClose,
  className = "",
  onClose,
  id,
  pauseOnFocusLoss,
  pauseOnHover,
  disableAnimation = false
}: ToastOptions) => {
  const content = title ? (
    <ToastContent title={title} message={message} />
  ) : (
    message
  );
  const animationOptions = disableAnimation
    ? { transition: ToastNoTransition }
    : {};

  switch (type) {
    case EToastType.LOADING: {
      toast.dismiss();
      return toast.loading(content, {
        ...(id && { toastId: id }),
        className: twMerge(
          "toast-custom toast-loading",
          disableAnimation && "toast-no-animation",
          className
        ),
        icon: icon ?? <SpinnerIcon />,
        autoClose: autoClose ?? false,
        pauseOnFocusLoss,
        pauseOnHover,
        ...animationOptions
      });
    }
    case EToastType.ERROR: {
      toast.dismiss();
      return toast.error(content, {
        ...(id && { toastId: id }),
        className: twMerge(
          "toast-custom toast-error",
          disableAnimation && "toast-no-animation",
          className
        ),
        icon: icon || <CircleCross className="fill-cinnabar" />,
        autoClose: autoClose ?? 3000,
        onClose,
        pauseOnFocusLoss,
        pauseOnHover,
        ...animationOptions
      });
    }
    case EToastType.SUCCESS: {
      toast.dismiss();
      return toast.success(content, {
        ...(id && { toastId: id }),
        className: twMerge(
          "toast-custom toast-success",
          disableAnimation && "toast-no-animation",
          className
        ),
        icon: icon || <CircleCheckIcon className="fill-shamrock-green" />,
        autoClose: autoClose ?? 3000,
        onClose,
        pauseOnFocusLoss,
        pauseOnHover,
        ...animationOptions
      });
    }
    case EToastType.WARNING: {
      toast.dismiss();
      return toast.warning(content, {
        ...(id && { toastId: id }),
        className: twMerge(
          "toast-custom toast-warning",
          disableAnimation && "toast-no-animation",
          className
        ),
        icon: icon || <CircleInformation className="fill-yellow-600" />,
        autoClose: autoClose ?? 3000,
        pauseOnFocusLoss,
        pauseOnHover,
        ...animationOptions
      });
    }
  }
};

// Hook for component use (unchanged API)
const useToast = () => ({ showToast });

export default useToast;
